import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPendingRequests,
  createSupportSession,
  fetchCannedResponses,
  fetchSupportSessions,
  fetchSupportMessages,
} from '../../store/Slices/supportSlice';
import { Box, Typography, List, ListItem, ListItemText, Button, Card, CardContent, Snackbar, AppBar, Toolbar, IconButton } from '@mui/material';
import socket from '../../services/socket';
import { useNavigate } from 'react-router-dom';
import { lightTheme, darkTheme } from '../../themes/themes';
import HomeIcon from '@mui/icons-material/Home'; // أيقونة الصفحة الرئيسية

const StaffSupportDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const { pendingRequests, sessions, cannedResponses } = useSelector((state) => state.support);
  const isLightTheme = useSelector((state) => state.theme.light);
  const theme = isLightTheme ? lightTheme : darkTheme;
  const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (user?._id && !isInitialFetchDone) {
      dispatch(fetchPendingRequests());
      dispatch(fetchSupportSessions(user._id)).then((result) => {
        const fetchedSessions = result.payload;
        if (fetchedSessions && fetchedSessions.length > 0) {
          fetchedSessions.forEach((session) => {
            dispatch(fetchSupportMessages(session._id));
          });
        }
        setIsInitialFetchDone(true);
      });
      dispatch(fetchCannedResponses());

      socket.emit('join', { user_id: user._id });
    }

    socket.on('new_support_request', () => {
      dispatch(fetchPendingRequests());
    });
    socket.on('message', (message) => {
      dispatch({ type: 'support/addMessage', payload: message });
    });
    socket.on('session_started', ({ session_id, support_staff_id }) => {
      if (support_staff_id === user._id) {
        dispatch(fetchSupportSessions(user._id));
        dispatch(fetchSupportMessages(session_id));
      }
    });

    socket.on('session_ended', ({ support_session_id, message }) => {
      console.log(`Staff received session_ended event: ${message} for session ${support_session_id}`);
      setSnackbarMessage(message);
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/staff/support-dashboard');
      }, 2000);
    });

    return () => {
      socket.off('new_support_request');
      socket.off('message');
      socket.off('session_started');
      socket.off('session_ended');
    };
  }, [dispatch, user, navigate, isInitialFetchDone]);

  const handleStartSession = (requestId) => {
    const payload = { support_request_id: requestId, support_staff_id: user._id };
    console.log('Sending payload to server:', payload);
    dispatch(createSupportSession(payload));
  };

  const handleOpenChat = (sessionId) => {
    navigate(`/support/session/${sessionId}`);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleHomeClick = () => {
    navigate('/home'); // التنقل إلى الصفحة الرئيسية
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* إضافة Navbar */}
      <AppBar
        position="static"
        sx={{
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 'bold',
            }}
          >
            Support Dashboard
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleHomeClick}
            sx={{
              color: theme.palette.brand.main,
              '&:hover': { color: '#d4a027' },
            }}
          >
            <HomeIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* المحتوى الرئيسي */}
      <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
        <Card
          sx={{
            mb: 4,
            bgcolor: theme.palette.background.paper,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
              Pending Requests
            </Typography>
            <List>
              {pendingRequests.length === 0 ? (
                <Typography sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
                  No pending requests currently
                </Typography>
              ) : (
                pendingRequests.map((request) => (
                  <ListItem
                    key={request._id}
                    sx={{
                      bgcolor: theme.palette.background.paper,
                      borderRadius: '8px',
                      mb: 1,
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      '&:hover': { bgcolor: theme.palette.action.hover },
                    }}
                    secondaryAction={
                      <Button
                        variant="contained"
                        onClick={() => handleStartSession(request._id)}
                        sx={{
                          bgcolor: theme.palette.brand.main,
                          color: '#fff',
                          '&:hover': { bgcolor: '#d4a027' },
                          borderRadius: '6px',
                        }}
                      >
                        Start Session
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={`${request.subject} - From: ${request.user_id?.username || 'Unknown User'}`}
                      secondary={request.description}
                      primaryTypographyProps={{ color: theme.palette.text.primary }}
                      secondaryTypographyProps={{ color: theme.palette.text.secondary }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </CardContent>
        </Card>

        <Card
          sx={{
            bgcolor: theme.palette.background.paper,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
              Active Sessions
            </Typography>
            {sessions.length === 0 ? (
              <Typography sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
                No active sessions currently
              </Typography>
            ) : (
              sessions.map((session) => (
                <Box
                  key={session._id}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: `1px solid ${theme.palette.divider || '#ddd'}`,
                    borderRadius: '8px',
                    bgcolor: theme.palette.background.paper,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                      {session.support_request_id?.subject || 'Support Session'}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => handleOpenChat(session._id)}
                      sx={{
                        bgcolor: theme.palette.brand.main,
                        color: '#fff',
                        '&:hover': { bgcolor: '#d4a027' },
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        px: 2,
                        py: 0.5,
                      }}
                    >
                      Open Chat
                    </Button>
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          sx={{ '& .MuiSnackbarContent-root': { bgcolor: theme.palette.brand.main, color: '#fff' } }}
        />
      </Box>
    </Box>
  );
};

export default StaffSupportDashboard;